import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row } from 'antd';
import classNames from 'classnames/bind';
import { Dayjs } from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Permission from '~/components/Common/Permission';
import RangePicker from '~/components/Common/RangePicker';
import { RoleName } from '~/models/common';
import FormItem from '~/components/Common/FormItem';

import styles from './index.module.scss';

export interface FilterListEventProps {
  handleFilter: (values: FilterListEventFormValues) => void;
  isAdmin?: boolean;
}

export type FilterListEventFormValues = {
  companyId?: string;
  companyName?: string;
  name?: string;
  createdAt?: [Dayjs, Dayjs];
  schedule?: [Dayjs, Dayjs];
};

const cx = classNames.bind(styles);
const FilterListEvent = ({
  handleFilter,
}: FilterListEventProps): React.ReactElement => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FilterListEventFormValues>();

  const onFinish = (values: any): void => {
    handleFilter(values);
  };

  return (
    <div className={cx('filter-wrapper')}>
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Row gutter={[32, 32]}>
          <Permission roles={[RoleName.SystemAdmin]}>
            <Col lg={{ span: 4 }} sm={{ span: 8 }}>
              <FormItem
                isCheckFullWidth
                name="companyId"
                className={cx('form-item')}
                label={t('event.list.company-id-label')}
              >
                <Input
                  className={cx('input-item')}
                  placeholder={t('event.list.search-placeholder')!}
                  suffix={<SearchOutlined className={cx('icon')} />}
                />
              </FormItem>
            </Col>
            <Col lg={{ span: 4 }} sm={{ span: 8 }}>
              <FormItem
                name="companyName"
                className={cx('form-item')}
                label={t('event.list.company-name-label')}
              >
                <Input
                  className={cx('input-item')}
                  placeholder={t('event.list.search-placeholder')!}
                  suffix={<SearchOutlined className={cx('icon')} />}
                />
              </FormItem>
            </Col>
          </Permission>
          <Col lg={{ span: 4 }} sm={{ span: 8 }}>
            <FormItem
              name="name"
              className={cx('form-item')}
              label={t('event.list.name-label')}
            >
              <Input
                className={cx('input-item')}
                placeholder={t('event.list.search-placeholder')!}
                suffix={<SearchOutlined className={cx('icon')} />}
              />
            </FormItem>
          </Col>
          <Col lg={{ span: 8 }} sm={{ span: 8 }}>
            <div className={cx('value-wrapper')}>
              <FormItem
                name="createdAt"
                className={cx('form-item')}
                label={t('event.list.created-at-label')}
              >
                <RangePicker
                  style={{
                    flex: 1,
                  }}
                  placeholder={[
                    t('event.list.created-at-placeholder'),
                    t('event.list.created-at-placeholder'),
                  ]}
                />
              </FormItem>
            </div>
          </Col>
          <Col lg={{ span: 8 }} sm={{ span: 8 }}>
            <div className={cx('value-wrapper')}>
              <FormItem
                name="schedule"
                className={cx('form-item')}
                label={t('event.list.schedule-label')}
              >
                <RangePicker
                  style={{
                    flex: 1,
                  }}
                  placeholder={[
                    t('event.list.start-date-placeholder'),
                    t('event.list.end-date-placeholder'),
                  ]}
                />
              </FormItem>
            </div>
          </Col>
        </Row>
        <Row>
          <Col
            className={cx('btn-wrapper')}
            lg={{ span: 5, offset: 19 }}
            sm={{ span: 10, offset: 14 }}
          >
            <Button
              className={cx('btn')}
              onClick={(): void => {
                form.resetFields();
                form.submit();
              }}
            >
              {t('metType.list.btn-clear')}
            </Button>
            <Button type="primary" htmlType="submit" onClick={form.submit}>
              {t('metType.list.btn-search')}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default FilterListEvent;
